/**
 * "Start from git": one or more git repositories, public or private, from any git servers, each cloned into its own
 * service (`services/<name>`, the name read from the repository, editable, unique) with vfs-service's clone (POST
 * /clone, the MCP tool vfs_clone's REST twin) with keepRemote, so pull and push work on it later. A private repository
 * uses the person's connected account for its host (GET /api/v1/me/accounts, Features › Connections): the default one,
 * or the one picked. An SSH URL is cloned over HTTPS by vfs-service.
 *
 * Used in three places: New project (the project is created first, `ensureProject`), a brand-new project's "Start by…"
 * and the Home's Apps "+" (`StartFromGitDialog`, into the project that is open). Repositories are cloned one after the
 * other with their own progress; one that fails keeps its error (and a Connect link when it needs an account) and can
 * be retried, without undoing the others or the project. Credentials never go in a URL: such a URL is refused here.
 * The Files tab's "Add repo as context…" uses the same form for `context/<name>` (`folder`), with its own clone
 * (`clone`: vfs-service's POST /context-repos) and words (`hint`).
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, ConfigProvider, Modal, theme as antdTheme } from 'antd';
import { Plus, X } from '@phosphor-icons/react';
import { listAccounts } from '../../services/AccountsService';
import { cloneRepoIntoProject } from '../../utils/gitTemplateClone';
import * as vfsClient from '../../utils/vfsClient';
import { accountsForHost, parseRepoUrl, repoNameFromUrl, uniqueServiceName, validateServiceName } from '../../utils/gitRepoSource';
import './NewProjectDialog.css';
import './ImportProjectDialog.css';
import './StartFromGit.css';

const CONNECTIONS_PATH = '/features/connections';
const NEEDS_ACCOUNT = new Set(['no_account', 'account_refused', 'accounts_off', 'no_person']);

let nextKey = 1;
const newRow = () => ({ key: nextKey++, url: '', name: '', nameEdited: false, account: '', status: 'idle', error: null, code: null, connectUrl: null, usedAccount: null });

/** The names the other rows hold, plus the project's own services. */
const takenFor = (rows, row, takenNames) => [...takenNames, ...rows.filter((other) => other.key !== row.key).map((other) => other.name).filter(Boolean)];

/** Why a row cannot be cloned yet, or null. */
function rowProblem(rows, row, takenNames, folder = 'services') {
  const parsed = parseRepoUrl(row.url);
  if (parsed.error) { return { field: 'url', text: parsed.error }; }
  const nameError = validateServiceName(row.name, takenFor(rows, row, takenNames), folder);
  return nameError ? { field: 'name', text: nameError } : null;
}

export default function StartFromGit({
  ensureProject, takenNames = [], onCloned = null, onFinished, onCancel, onSuggestName = null, submitLabel = 'Clone', disabled = false,
  folder = 'services', clone = null, hint = null,
}) {
  const [rows, setRows] = useState(() => [newRow()]);
  const [touched, setTouched] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [accounts, setAccounts] = useState({ list: [], loaded: false, note: null });
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // A clone outlives the render that started it: the latest callbacks are called when it ends.
  const callbacks = useRef({ onCloned, onFinished });
  callbacks.current = { onCloned, onFinished };

  const loadAccounts = useCallback(async() => {
    try {
      const answer = await listAccounts();
      setAccounts({ list: answer?.accounts || [], loaded: true, note: null });
    } catch (err) {
      const off = err?.code === 'accounts_off' || err?.status === 404;
      setAccounts({ list: [], loaded: true, note: off ? 'Accounts are not enabled on this Flow: only public repositories can be cloned.' : 'Your accounts could not be loaded: public repositories still clone.' });
    }
  }, []);
  useEffect(() => { loadAccounts(); }, [loadAccounts]);

  // The ref is kept in step at once: a clone in progress reads the rows before React renders them.
  const commit = (next) => { rowsRef.current = next; setRows(next); };
  const update = (key, changes) => commit(rowsRef.current.map((row) => (row.key === key ? { ...row, ...changes } : row)));

  const setUrl = (row, url) => {
    const changes = { url, error: null, code: null, connectUrl: null, status: row.status === 'failed' ? 'idle' : row.status };
    if (!row.nameEdited) {
      const base = repoNameFromUrl(url);
      changes.name = base ? uniqueServiceName(base, takenFor(rowsRef.current, row, takenNames)) : '';
    }
    update(row.key, changes);
  };

  const pending = rows.filter((row) => row.status !== 'done');
  const done = rows.filter((row) => row.status === 'done');
  const failed = rows.filter((row) => row.status === 'failed');

  /** The project to clone into: made once (New project), then reused by every retry. */
  const project = async() => {
    if (projectId) { return projectId; }
    const id = await ensureProject();
    if (id) { setProjectId(id); }
    return id || null;
  };

  const cloneOne = async(id, row) => {
    update(row.key, { status: 'cloning', error: null, code: null, connectUrl: null });
    const subfolder = `${folder}/${row.name.trim()}`;
    try {
      const args = { repoUrl: row.url.trim(), subfolder, account: row.account || null };
      const result = clone ? await clone(id, { ...args, name: row.name.trim() }) : await cloneRepoIntoProject(id, args);
      if (folder === 'services') { vfsClient.registerVfsService(id, subfolder); }
      update(row.key, { status: 'done', usedAccount: result?.usedAccount || null, note: result?.note || null });
      callbacks.current.onCloned?.(id, subfolder);
      return true;
    } catch (err) {
      update(row.key, { status: 'failed', error: err?.message || 'The clone failed.', code: err?.code || null, connectUrl: err?.connectUrl || null });
      return false;
    }
  };

  const run = async(only = null) => {
    setTouched(true);
    setError(null);
    const current = rowsRef.current;
    const targets = current.filter((row) => (only ? row.key === only.key : row.status !== 'done'));
    if (!targets.length || targets.some((row) => rowProblem(current, row, takenNames, folder))) { return; }
    setRunning(true);
    let id;
    try {
      id = await project();
    } catch (err) {
      setError(err?.message || 'The project could not be created. Try again.');
      setRunning(false);
      return;
    }
    if (!id) { setRunning(false); return; }
    if (only) { await loadAccounts(); }
    let allOk = true;
    for (const row of targets) {
      // eslint-disable-next-line no-await-in-loop -- one clone at a time: its own progress, and the sandbox is not flooded
      const ok = await cloneOne(id, rowsRef.current.find((r) => r.key === row.key) || row);
      allOk = allOk && ok;
    }
    setRunning(false);
    const stillOpen = rowsRef.current.some((row) => row.status !== 'done' && !targets.some((t) => t.key === row.key));
    if (allOk && !stillOpen) { finish(id); }
  };

  const finish = (id = projectId) => {
    const services = rowsRef.current.filter((row) => row.status === 'done').map((row) => `${folder}/${row.name.trim()}`);
    callbacks.current.onFinished({ projectId: id, services });
  };

  const remove = (key) => { if (rowsRef.current.length > 1) { commit(rowsRef.current.filter((row) => row.key !== key)); } };
  const cloning = rows.find((row) => row.status === 'cloning');
  const index = cloning ? rows.indexOf(cloning) + 1 : 0;

  return (
    <div className="sbx-git" data-testid="start-from-git">
      {hint || <p className="sbx-new-project-hint">Each repository becomes a service under <code>services/</code>, with its history and its remote kept, so you can pull and push later. Private repositories use your connected account for their server.</p>}
      {accounts.note && <p className="sbx-import-muted sbx-git-note">{accounts.note}</p>}
      <ol className="sbx-git-repos">
        {rows.map((row, position) => {
          const n = position + 1;
          const problem = touched && row.status !== 'done' ? rowProblem(rows, row, takenNames, folder) : null;
          const parsed = parseRepoUrl(row.url);
          const forHost = parsed.error ? [] : accountsForHost(accounts.list, parsed.host);
          const locked = row.status === 'done' || row.status === 'cloning' || running;
          const connectHref = row.connectUrl || CONNECTIONS_PATH;
          return (
            <li className={`sbx-git-repo is-${row.status}`} data-testid="git-repo" key={row.key}>
              <div className="sbx-git-row">
                <label className="sbx-new-project-label" htmlFor={`sbx-git-url-${row.key}`}>Repository {rows.length > 1 ? n : ''}</label>
                {rows.length > 1 && row.status !== 'done' && (
                  <button aria-label={`Remove repository ${n}`} className="sbx-git-remove" disabled={locked} onClick={() => remove(row.key)} type="button"><X aria-hidden size={14} /></button>
                )}
              </div>
              <input
                aria-invalid={problem?.field === 'url' ? 'true' : 'false'}
                aria-label={`Repository ${n} URL`}
                autoComplete="off"
                className={`sbx-new-project-input${problem?.field === 'url' ? ' invalid' : ''}`}
                disabled={locked || disabled}
                id={`sbx-git-url-${row.key}`}
                onBlur={() => { if (position === 0 && row.name) { onSuggestName?.(row.name); } }}
                onChange={(event) => setUrl(row, event.target.value)}
                placeholder="https://github.com/acme/shop.git or git@gitlab.com:acme/shop.git"
                spellCheck={false}
                type="text"
                value={row.url}
              />
              <div className="sbx-git-fields">
                <label className="sbx-git-name">
                  <span className="sbx-git-prefix">{folder}/</span>
                  <input
                    aria-invalid={problem?.field === 'name' ? 'true' : 'false'}
                    aria-label={`${folder === 'services' ? 'Service name' : 'Name'} for repository ${n}`}
                    autoComplete="off"
                    className={`sbx-new-project-input${problem?.field === 'name' ? ' invalid' : ''}`}
                    disabled={locked || disabled}
                    onChange={(event) => update(row.key, { name: event.target.value, nameEdited: true })}
                    placeholder="name"
                    spellCheck={false}
                    type="text"
                    value={row.name}
                  />
                </label>
                {forHost.length > 0 && (
                  <select
                    aria-label={`Account for repository ${n}`}
                    className="sbx-new-project-input sbx-git-account"
                    disabled={locked || disabled}
                    onChange={(event) => update(row.key, { account: event.target.value })}
                    value={row.account}
                  >
                    <option value="">{forHost[0].isDefault ? `Your default: ${forHost[0].label || forHost[0].name}` : 'No account (public repository)'}</option>
                    {forHost.map((account) => <option key={account.id} value={account.id}>{account.name ? `${account.name} (${account.label || account.login})` : account.label}</option>)}
                  </select>
                )}
              </div>
              {problem && <p className="sbx-new-project-field-error" role="alert">{problem.text}</p>}
              {!problem && !parsed.error && forHost.length === 0 && accounts.loaded && !accounts.note && row.status === 'idle' && (
                <p className="sbx-import-muted">No account for {parsed.host}: fine for a public repository. Private? <a href={CONNECTIONS_PATH} rel="noreferrer" target="_blank">Connect one</a>.</p>
              )}
              {row.status === 'cloning' && <p className="sbx-import-muted" role="status">Cloning…</p>}
              {row.status === 'done' && <p className="sbx-git-done" role="status">Cloned into {folder}/{row.name}{row.usedAccount ? ` with ${row.usedAccount}` : ''}.{row.note ? ` ${row.note}` : ''}</p>}
              {row.status === 'failed' && (
                <div className="sbx-new-project-error sbx-git-failed" role="alert">
                  <span>{row.error}</span>
                  <span className="sbx-git-failed-actions">
                    {NEEDS_ACCOUNT.has(row.code) && <a href={connectHref} rel="noreferrer" target="_blank">Connect an account</a>}
                    <Button disabled={running} onClick={() => run(row)} size="small">Retry</Button>
                  </span>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <button className="sbx-templates-toggle" disabled={running || disabled} onClick={() => commit([...rowsRef.current, newRow()])} type="button">
        <Plus aria-hidden size={12} /> Add another repository
      </button>
      {running && cloning && <p className="sbx-import-muted" role="status">Cloning {index} of {rows.length}…</p>}
      {error && <p className="sbx-new-project-error" role="alert">{error}</p>}
      <div className="sbx-new-project-actions">
        {projectId
          ? <Button disabled={running} onClick={() => finish()}>{done.length ? `Continue with ${done.length} cloned` : 'Continue without them'}</Button>
          : <Button disabled={running} onClick={onCancel}>Cancel</Button>}
        <Button disabled={disabled || pending.length === 0} loading={running} onClick={() => run()} type="primary">
          {running ? 'Cloning…' : failed.length > 0 ? 'Retry failed' : submitLabel}
        </Button>
      </div>
    </div>
  );
}

StartFromGit.propTypes = {
  // Instead of the service clone: (projectId, { repoUrl, name, subfolder, account }) => its answer (Files' context repos).
  clone: PropTypes.func,
  disabled: PropTypes.bool,
  // The project to clone into: resolves to its id (created on first use in New project), or null to stop.
  ensureProject: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  // Each service cloned: (projectId, 'services/<name>').
  onCloned: PropTypes.func,
  // Done (all cloned, or the person continues without the failed ones): { projectId, services }.
  onFinished: PropTypes.func.isRequired,
  // The first repository's name, for a project name still empty.
  onSuggestName: PropTypes.func,
  // Where each repository goes: `services` (a service) or `context` (a context repo).
  folder: PropTypes.oneOf(['services', 'context']),
  // The words above the form, instead of the services' ones.
  hint: PropTypes.node,
  submitLabel: PropTypes.string,
  // The project's services' short names (a new one must not reuse them).
  takenNames: PropTypes.arrayOf(PropTypes.string),
};

/** StartFromGit in its own dialog, into a project that exists (the Home's "Start by…" and Apps "+"). */
export function StartFromGitDialog({ open, onClose, projectId, takenNames = [], onCloned = null, onFinished = null }) {
  const isDark = typeof document !== 'undefined' && document.documentElement.getAttribute('data-theme') === 'dark';
  return (
    <ConfigProvider theme={{ algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm }}>
      <Modal className="sbx-new-project sbx-git-dialog" destroyOnHidden footer={null} onCancel={onClose} open={open} title="Add from a repository" width={560}>
        <StartFromGit
          ensureProject={async() => projectId}
          onCancel={onClose}
          onCloned={onCloned}
          onFinished={(result) => { onFinished?.(result); onClose(); }}
          takenNames={takenNames}
        />
      </Modal>
    </ConfigProvider>
  );
}

StartFromGitDialog.propTypes = {
  onCloned: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onFinished: PropTypes.func,
  open: PropTypes.bool.isRequired,
  projectId: PropTypes.string.isRequired,
  takenNames: PropTypes.arrayOf(PropTypes.string),
};
